import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { issueReportService } from '@/services';
import { 
  CreateIssueReportRequest, 
  UpdateIssueReportRequest,
  IssueReportFilters,
  IssuePriority
} from '@/types/model';
import { QueryParams } from '@/services/apiClient';

export const issueReportKeys = {
  all: ['issue-reports'] as const,
  lists: () => [...issueReportKeys.all, 'list'] as const,
  list: (params: QueryParams & IssueReportFilters) => [...issueReportKeys.lists(), params] as const,
  details: () => [...issueReportKeys.all, 'detail'] as const,
  detail: (id: string) => [...issueReportKeys.details(), id] as const,
  stats: () => [...issueReportKeys.all, 'stats'] as const,
  counts: () => [...issueReportKeys.all, 'counts'] as const,
};

export const useIssueReports = (params: QueryParams & IssueReportFilters = {}) => {
  const queryResult = useQuery({
    queryKey: issueReportKeys.list(params),
    queryFn: () => issueReportService.getAll(params),
    staleTime: 2 * 60 * 1000, // 2 minutes - fresher data for issue reports
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useIssueReport = (id: string) => {
  return useQuery({
    queryKey: issueReportKeys.detail(id),
    queryFn: () => issueReportService.getById(id),
    enabled: !!id,
  });
};

export const useIssueReportStats = () => {
  return useQuery({
    queryKey: issueReportKeys.stats(),
    queryFn: () => issueReportService.getStats(),
    staleTime: 1 * 60 * 1000, // 1 minute for stats
  });
};

export const useIssueReportCounts = () => {
  return useQuery({
    queryKey: issueReportKeys.counts(),
    queryFn: () => issueReportService.getCounts(),
    staleTime: 1 * 60 * 1000, // 1 minute for counts
  });
};

export const useCreateIssueReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateIssueReportRequest) =>
      issueReportService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.stats() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.counts() });
    },
  });
};

export const useUpdateIssueReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateIssueReportRequest }) =>
      issueReportService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.stats() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.counts() });
    },
  });
};

export const useDeleteIssueReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => issueReportService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.stats() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.counts() });
    },
  });
};

export const useMarkIssueAsResolved = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      issueReportService.markAsResolved(id, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.stats() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.counts() });
    },
  });
};

export const useMarkIssueAsInProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, assignedTo }: { id: string; assignedTo?: string }) =>
      issueReportService.markAsInProgress(id, assignedTo),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.stats() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.counts() });
    },
  });
};

export const useMarkIssueAsRejected = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      issueReportService.markAsRejected(id, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.stats() });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.counts() });
    },
  });
};

export const useAssignIssue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, assignedTo }: { id: string; assignedTo: string }) =>
      issueReportService.assignTo(id, assignedTo),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.lists() });
    },
  });
};

export const useUpdateIssuePriority = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: IssuePriority }) =>
      issueReportService.updatePriority(id, priority),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: issueReportKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: issueReportKeys.lists() });
    },
  });
};